// Japanese (です/ます) copy for /web-uploader, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "Webアップローダー" as in the site's footer (src/i18n/locales/ja.json); "プレビュー" and "オリジナル".
// Round figures in 万 (1万枚, 10万枚), as Japanese copy normally writes them.
// The uploader's own labels are in English, so they're quoted as it shows them (「Previews」「Originals」).
// The text inside the two animations stays in English, like the screenshots (the uploader is in English);
// their captions, legends and accessible names are translated.
import type { WebUploaderCopy } from "./copy.en";
import en from "./copy.en";

const ja: WebUploaderCopy = {
  seo: {
    title: "新しいPhotohawk Webアップローダー | FTPより速く、大規模イベントにも対応",
    description:
      "PhotohawkのWebアップローダーを一新しました。多数の写真を同時に送信し、10万枚を超えるアップロードにも対応。接続が切れても自動で再開します。すべてのプランでご利用いただけます。すべてのプランで軽いプレビューを先にアップロードするので、ギャラリーを数時間ではなく数分で公開できます。",
  },
  hero: {
    badge: "新機能",
    eyebrow: "すべてのプランで利用可能",
    title: "新しいPhotohawk Webアップローダー",
    subtitle:
      "FTPより速く、10万枚を超えるアップロードにも対応し、中断したところから再開します。",
    plans: "すべてのプランで使えるプレビュー先行なら、大きなイベントも数時間ではなく数分で公開できます。",
    ctaPrimary: "Webアップローダーを開く",
    ctaSecondary: "新機能を見る",
    screenshotAlt:
      "アップロード中のWebアップローダー。プレビュー先行（すべてのプラン）の場合、ステータスバーにはギャラリーがプレビューで公開中であることと現在の訪問者数が表示され、右側のキューには完了したプレビューと送信中のオリジナルが並び、購入された2枚の写真が先に送られています。",
    phoneAlt: "スマートフォンで開いたWebアップローダー。ギャラリー内のフォルダを表示し、画面下部にアップロードの状況が出ています。",
  },
  previews: {
    heading: "プレビューを先に",
    badge: "すべてのプラン",
    subtitle:
      "すべてのプランで、数時間ではなく数分で公開できます。1万枚のギャラリーでも、アップロード速度20 Mbpsで約8分で公開し、販売を始められます。",
    body: "軽いプレビューを先にアップロードするので、オリジナルの送信を待たずにギャラリーを公開し、販売や無料ダウンロードの提供を始められます。オリジナルが届く前に誰かが写真を購入またはダウンロードすると、その写真のオリジナルがキューの先頭に回ります。",
    steps: [
      { id: "previews", text: "軽いプレビューを先にアップロード" },
      { id: "live", text: "ギャラリーを公開" },
      { id: "buy", text: "誰かが写真を購入" },
      { id: "jump", text: "その写真のオリジナルがキューの先頭へ" },
      { id: "originals", text: "残りのオリジナルが続けて届く" },
    ],
    anim: {
      ...en.previews.anim,
      label: "プレビューを先に送る仕組みのアニメーション",
      description:
        "数枚の写真の軽いプレビューがパソコンからギャラリーへ送られ、ギャラリーが「Live」に切り替わります。誰かが1枚の写真で「Buy」をタップすると、そのオリジナルがオリジナルのキューの先頭に移り、最初にアップロードされます。残りのオリジナルがその後に続きます。",
    },
    link: "プレビュー先行アップロードの仕組み",
  },
  faster: {
    heading: "FTPより速い理由",
    subtitle: "同じ写真を、2つの方法で送った場合。",
    anim: {
      ...en.faster.anim,
      label: "FTPソフトとWebアップローダーを比べたアニメーション",
      description:
        "2つの列があります。上の列では、FTPソフトが初期設定では同時に2枚、上限は10枚で写真を送ります。下の列では、Webアップローダーが多数の写真を同時に送り、送信レーンは接続状況に合わせて広がったり狭まったりします。すべてのプランで軽いプレビューが先に届くので、ギャラリーがより早く公開されます。途中で接続が一瞬切れても、アップロードは自動で再開します。",
      legendPreviews: "プレビュー",
      legendOriginals: "オリジナル",
    },
    points: [
      {
        id: "parallel",
        title: "多数の写真を同時に送信",
        body: "多数の写真を同時に送信します。FileZilla（初期設定では同時に2枚、上限は10枚）のようなFTPソフトよりはるかに多い枚数です。",
      },
      {
        id: "adaptive",
        title: "回線を最大限に活用",
        body: "アップロード中も常に調整を続け、回線を最大限に活用します。",
      },
      {
        id: "scale",
        title: "大規模イベントのために設計",
        body: "大規模なテストを行い、10万枚を超えるアップロードにも余裕で対応できることを確認しています。",
      },
      {
        id: "live",
        title: "数時間ではなく、数分で公開（すべてのプラン）",
        body: "すべてのプランで軽いプレビューを先にアップロードするので、1万枚のギャラリーでもアップロード速度20 Mbpsで約8分で公開して販売を始められます。オリジナルはその後に届きます。",
      },
      {
        id: "resume",
        title: "中断したところから再開",
        body: "接続が切れても自動で再開します。同じフォルダをもう一度追加すれば、足りない分だけが送信されます。",
      },
    ],
  },
  /** The upload-time estimator (#estimate). {placeholders} are filled in by the page. */
  estimate: {
    heading: "アップロードにかかる時間は？",
    subtitle: "お使いの回線のアップロード速度から、ギャラリーのアップロード時間を見積もります。",
    heroLink: "アップロード時間を見積もる",
    photos: "写真の枚数",
    custom: "その他",
    customLabel: "写真の枚数（その他）",
    size: "オリジナル1枚の平均サイズ",
    sizeUnit: "MB",
    sizeHint: "イベント写真のJPEGは、多くの場合約{mb} MBです。",
    speed: "アップロード速度",
    speedUnit: "Mbps",
    test: "アップロード速度を測定",
    testing: "測定中… {mbps} Mbps",
    testStarting: "測定中…",
    testDone: "測定結果：アップロード {mbps} Mbps",
    testFailed: "現在、速度を測定できませんでした。ご自身で入力するか、一般的なスピードテストをお試しください。",
    dataNote: "使用するデータ量は最大で約{mb} MBで、ボタンをタップしたときだけ測定します。",
    otherTest: "一般的なスピードテストを使う",
    resultsHeading: "見積もり結果",
    previewsLabel: "プレビュー先行",
    previewsPlans: "すべてのプラン",
    previewsLive: "ギャラリー公開まで{time}",
    previewsAll: "すべてのオリジナルのアップロード完了まで{time}",
    originalsLabel: "オリジナルのみ",
    originalsLive: "ギャラリー公開まで{time}",
    originalsWhen: "（{gb} GBのオリジナルがすべてアップロードされた時点）",
    computerNote: "この速度では、パソコンがプレビューを作成する速さが全体のペースを決めます。",
    disclaimer: "あくまで見積もりです。実際の時間は、回線、パソコン、写真によって変わります。",
    time: {
      under: "1分未満",
      min: "約{n}分",
      h: "約{n}時間",
      d: "約{n}日",
    },
    table: {
      caption: "例：1万枚の写真",
      speed: "アップロード速度",
      live: "プレビュー先行：ギャラリー公開（すべてのプラン）",
      all: "すべてのオリジナルのアップロード完了",
      note: "約8分という数値は、アップロード速度20 Mbpsで測定したものです（プレビュー先行）。その他の行は同じモデルによる見積もりで、オリジナル1枚を約{mb} MBとしています。",
    },
  },
  features: {
    heading: "その他の新機能",
    subtitle: "新しいWebアップローダーの実際の画面です。",
    items: [
      {
        id: "status",
        title: "状況がひと目でわかる表示",
        body: "状況がひと目でわかります。プレビュー先行（すべてのプラン）では、「Previews」（プレビュー）、次に「Originals」（オリジナル）と、それぞれの進み具合を確認できます。",
        alt: "プレビュー先行（すべてのプラン）でのアップロードの状況：「Live — saving originals…」、Previews と Originals それぞれの進行状況の行、そして「2 photos people bought go first」。",
      },
      {
        id: "queue",
        title: "好きな場所に置けるキュー",
        body: "アップロードキューを右・左・下のいずれかに固定し、好きなサイズにドラッグできます。設定はブラウザが記憶します。",
        alt: "アップローダーの下部に固定したアップロードキュー。メニューが開き、右・下・左への移動を選べます。",
      },
      {
        id: "visitors",
        title: "閲覧中の人数がわかる",
        body: "ステータスバーに、今ギャラリーを見ている人数が表示されます。すべてのプランでご利用いただけます。",
        alt: "アップローダー上部のステータスバー：「Live on previews」（プレビュー先行）と訪問者数。",
      },
      {
        id: "resume",
        title: "中断したところから再開",
        body: "接続が切れても自動で再開します。同じフォルダをもう一度追加すれば、足りない分だけが送信されます。",
        alt: "フォルダのアップロードが完了していないことを知らせるアップローダー内のバナーと「Resume」ボタン。",
      },
      {
        id: "phone",
        title: "スマートフォンでも見やすい",
        body: "小さな画面ではフォルダのパスが折りたたまれて収まり、「…」をタップすると途中のフォルダが表示されます。写真の送り先がいつでもわかります。",
        alt: "スマートフォンのアップローダー：パスには現在のフォルダが表示され、「…」で上位フォルダの一覧が開きます。",
      },
    ],
  },
  ftp: {
    date: "2026年11月1日",
    heading: "11月1日からのFTPの変更点",
    body: "常時稼働の共有FTPサーバーは2026年11月1日に終了します。現在FTPをお使いの場合は、それまでに切り替えをお願いします。",
    points: [
      {
        id: "closes",
        title: "共有FTPは2026年11月1日に終了",
        body: "常時稼働の共有FTPサーバーは2026年11月1日に停止します。",
      },
      {
        id: "standard",
        title: "アップロードはWebアップローダーが標準に",
        body: "すべてのプランで、今日からご利用いただけます。",
      },
      {
        id: "ondemand",
        title: "FTPが欠かせない場合は",
        body: "すべてのプランで、Webアップローダーから専用のオンデマンドFTPサーバーを起動できます。アドレスは個人専用で、変わることはありません。操作がないまま1時間たつと自動で停止します。",
      },
    ],
    helpLink: "オンデマンドFTPの仕組み",
  },
  more: {
    previewFirstTitle: "プレビュー先行アップロードの仕組み",
    previewFirstBody: "すべてのプランで利用できます。プレビュー、公開、購入者に見える画面まで、順を追って説明します。",
  },
  cta: {
    heading: "次のアップロードでお試しください",
    body: "新しいWebアップローダーは、すべてのプランですぐにご利用いただけます。フォルダを追加して、ギャラリーが公開される様子をご覧ください。",
    button: "Webアップローダーを開く",
  },
  pause: "アニメーションを一時停止",
  play: "アニメーションを再生",
};

export default ja;
